import type { Species } from '../api/species';
import { resolveIncubationDays } from './speciesDisplayUtils';

/** Map API species record into edit-form values (including legacy alias fields). */
export function hydrateSpeciesForm(species: Species) {
  const incubation = resolveIncubationDays(species);
  const priceMin = species.priceRangeMin;
  const priceMax = species.priceRangeMax;
  const marketValueRange =
    priceMin != null && priceMax != null ? `${priceMin} – ${priceMax}` : '';

  return {
    commonName: species.commonName,
    genus: species.genus,
    species: species.species,
    subspecies: species.subspecies || '',
    domain: species.domain || '',
    kingdom: species.kingdom || '',
    phylum: species.phylum || '',
    class: species.class || '',
    family: species.family || '',
    order: species.order || '',
    typeCategory: species.typeCategory,
    originLocation: species.originLocation,
    size: species.size?.toString() || '',
    weight: species.weight?.toString() || '',
    colorDescription: species.colorDescription,
    captiveBreedingStatus: species.captiveBreedingStatus,
    longevity: species.longevity?.toString() || '',
    recommendedHousing: species.recommendedHousing,
    diet: species.diet,
    nestBoxSize: species.nestBoxSize,
    clutchSize: species.clutchSize?.toString() || '',
    fledgingAge: species.fledgingAge?.toString() || '',
    hatchingPeriod: String(species.hatchingPeriod ?? incubation ?? ''),
    daysBeforeBanding: species.daysBeforeBanding?.toString() || '',
    bandSize: species.bandSize,
    iucnStatus: species.iucnStatus,
    citesStatus: species.citesStatus,
    mutations: species.mutations?.join(', ') || '',

    breedingSeasonStart: species.breedingSeasonStart || '',
    breedingSeasonEnd: species.breedingSeasonEnd || '',
    incubationPeriod: species.incubationPeriod?.toString() || (incubation ? String(incubation) : ''),
    nestingBehavior: species.nestingBehavior || '',
    parentalCarePeriod: species.parentalCarePeriod?.toString() || '',
    sexualMaturityAge: species.sexualMaturityAge?.toString() || '',
    breedingPairCompatibility: species.breedingPairCompatibility || '',
    nestingMaterialPreferences: species.nestingMaterialPreferences || '',
    breedingDifficultyLevel: species.breedingDifficultyLevel || '',
    geneticDiversityConcerns: species.geneticDiversityConcerns || '',

    // Legacy form aliases → canonical entity fields
    lifespan: species.longevity?.toString() || species.lifespan?.toString() || '',
    dietRequirements: species.diet || species.dietRequirements || '',
    housingRequirements: species.recommendedHousing || species.housingRequirements || '',
    commonHealthIssues: species.commonHealthIssues || '',
    vaccinationRequirements: species.vaccinationRequirements || '',
    parasiteSusceptibility: species.parasiteSusceptibility || '',
    stressFactors: species.stressFactors || '',
    environmentalRequirements: species.environmentalRequirements || '',
    socialBehavior: species.socialBehavior || '',
    handlingGuidelines: species.handlingGuidelines || '',
    quarantinePeriod: species.quarantinePeriod?.toString() || '',
    veterinaryCareNeeds: species.handlingGuidelines || species.veterinaryCareNeeds || '',

    marketValueRange,
    marketDemandLevel: species.marketDemandLevel || '',
    legalStatus: species.exportRestrictions || species.legalStatus || '',
    exportRestrictions: species.exportRestrictions || '',
    marketTrends: species.marketTrends || '',
    priceRangeMin: species.priceRangeMin?.toString() || '',
    priceRangeMax: species.priceRangeMax?.toString() || '',
    buyerPreferences: species.buyerPreferences || '',
    competitionLevel: species.competitionLevel || '',
    seasonalDemandPatterns: species.seasonalDemandPatterns || '',

    aiDataSources: species.aiDataSources?.join(', ') || '',
    aiConfidenceScore: species.aiConfidenceScore?.toString() || '',
    aiExtractionNotes: species.aiExtractionNotes || '',
    manualReviewStatus: species.manualReviewStatus || '',
    aiProcessingVersion: species.aiProcessingVersion || '',
    lastAiUpdate:
      species.aiLastUpdated ||
      species.lastAiUpdate ||
      '',
  };
}

type SpeciesFormLike = ReturnType<typeof hydrateSpeciesForm>;

/** Build API patch from edit form — only persisted entity fields. */
export function buildSpeciesUpdatePayload(formData: SpeciesFormLike): Partial<Species> {
  const incubation = formData.incubationPeriod
    ? parseInt(formData.incubationPeriod, 10)
    : formData.hatchingPeriod
      ? parseInt(formData.hatchingPeriod, 10)
      : undefined;
  const hatching = formData.hatchingPeriod
    ? parseInt(formData.hatchingPeriod, 10)
    : incubation;

  return {
    commonName: formData.commonName,
    genus: formData.genus,
    species: formData.species,
    subspecies: formData.subspecies?.trim() || undefined,
    domain: formData.domain?.trim() || undefined,
    kingdom: formData.kingdom?.trim() || undefined,
    phylum: formData.phylum?.trim() || undefined,
    class: formData.class?.trim() || undefined,
    family: formData.family?.trim() || undefined,
    order: formData.order?.trim() || undefined,
    typeCategory: formData.typeCategory,
    originLocation: formData.originLocation,
    size: parseFloat(formData.size),
    weight: parseFloat(formData.weight),
    colorDescription: formData.colorDescription,
    captiveBreedingStatus: formData.captiveBreedingStatus,
    longevity: parseInt(formData.longevity, 10),
    recommendedHousing: formData.housingRequirements?.trim() || formData.recommendedHousing,
    diet: formData.dietRequirements?.trim() || formData.diet,
    nestBoxSize: formData.nestBoxSize,
    clutchSize: parseInt(formData.clutchSize, 10),
    fledgingAge: parseInt(formData.fledgingAge, 10),
    hatchingPeriod: hatching ?? 0,
    daysBeforeBanding: formData.daysBeforeBanding ? parseInt(formData.daysBeforeBanding, 10) : 0,
    bandSize: formData.bandSize,
    iucnStatus: formData.iucnStatus,
    citesStatus: formData.citesStatus,
    // mutations are synced from the genetics catalog — not edited via this form

    breedingSeasonStart: formData.breedingSeasonStart || undefined,
    breedingSeasonEnd: formData.breedingSeasonEnd || undefined,
    incubationPeriod: incubation,
    nestingBehavior: formData.nestingBehavior || undefined,
    parentalCarePeriod: formData.parentalCarePeriod ? parseInt(formData.parentalCarePeriod, 10) : undefined,
    sexualMaturityAge: formData.sexualMaturityAge ? parseInt(formData.sexualMaturityAge, 10) : undefined,
    breedingPairCompatibility: formData.breedingPairCompatibility || undefined,
    nestingMaterialPreferences: formData.nestingMaterialPreferences || undefined,
    breedingDifficultyLevel: formData.breedingDifficultyLevel || undefined,
    geneticDiversityConcerns: formData.geneticDiversityConcerns || undefined,

    commonHealthIssues: formData.commonHealthIssues || undefined,
    vaccinationRequirements: formData.vaccinationRequirements || undefined,
    parasiteSusceptibility: formData.parasiteSusceptibility || undefined,
    stressFactors: formData.stressFactors || undefined,
    environmentalRequirements: formData.environmentalRequirements || undefined,
    socialBehavior: formData.socialBehavior || undefined,
    handlingGuidelines:
      formData.veterinaryCareNeeds?.trim() || formData.handlingGuidelines || undefined,
    quarantinePeriod: formData.quarantinePeriod ? parseInt(formData.quarantinePeriod, 10) : undefined,

    marketDemandLevel: formData.marketDemandLevel || undefined,
    priceRangeMin: formData.priceRangeMin ? parseFloat(formData.priceRangeMin) : undefined,
    priceRangeMax: formData.priceRangeMax ? parseFloat(formData.priceRangeMax) : undefined,
    marketTrends: formData.marketTrends || undefined,
    exportRestrictions: formData.exportRestrictions?.trim() || formData.legalStatus?.trim() || undefined,
    buyerPreferences: formData.buyerPreferences || undefined,
    competitionLevel: formData.competitionLevel || undefined,
    seasonalDemandPatterns: formData.seasonalDemandPatterns || undefined,

    aiDataSources: formData.aiDataSources
      ? formData.aiDataSources.split(',').map((s) => s.trim()).filter(Boolean)
      : undefined,
    aiConfidenceScore: formData.aiConfidenceScore ? parseFloat(formData.aiConfidenceScore) : undefined,
    aiExtractionNotes: formData.aiExtractionNotes || undefined,
    manualReviewStatus: formData.manualReviewStatus || undefined,
    aiProcessingVersion: formData.aiProcessingVersion || undefined,
  };
}
